<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  showTitle: {
    type: Boolean,
    default: true,
  },
  editable: {
    type: Boolean,
    default: false,
  },
  updateHistory: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['updated']);

const { t } = useI18n();
const store = useStore();

const ticketAttributes = useMapGetter('attributes/getTicketAttributes');
const loading = computed(() => store.getters['tickets/getUIFlags'].isUpdating);

const editingAttribute = ref(null);
const editingValue = ref(null);

const extractDateParts = value => {
  if (typeof value !== 'string') return null;

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  return {
    year: match[1],
    month: match[2],
    day: match[3],
  };
};

const formatDateForInput = value => {
  const parts = extractDateParts(value);
  if (parts) {
    return `${parts.year}-${parts.month}-${parts.day}`;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return null;

  const year = parsedDate.getUTCFullYear();
  const month = String(parsedDate.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = value => {
  const parts = extractDateParts(value);
  if (parts) {
    return `${parts.day}/${parts.month}/${parts.year}`;
  }

  const inputDate = formatDateForInput(value);
  if (!inputDate) return '—';

  const [year, month, day] = inputDate.split('-');
  return `${day}/${month}/${year}`;
};

const editingValueModel = computed({
  get() {
    if (!editingAttribute.value) return editingValue.value;

    const { attributeDisplayType } = editingAttribute.value;

    // For date inputs, convert ISO string back to YYYY-MM-DD format
    if (attributeDisplayType === 'date' && editingValue.value) {
      return formatDateForInput(editingValue.value) || '';
    }

    // For number inputs, return the number value (or empty string if null)
    if (attributeDisplayType === 'number') {
      return editingValue.value === null || editingValue.value === undefined
        ? ''
        : editingValue.value;
    }

    return editingValue.value;
  },
  set(newValue) {
    if (!editingAttribute.value) {
      editingValue.value = newValue;
      return;
    }

    const { attributeDisplayType } = editingAttribute.value;

    switch (attributeDisplayType) {
      case 'number': {
        // Handle empty values
        if (newValue === '' || newValue === null || newValue === undefined) {
          editingValue.value = null;
          return;
        }
        // Convert to number explicitly (HTML inputs return strings)
        const numValue =
          typeof newValue === 'string'
            ? parseFloat(newValue)
            : Number(newValue);
        if (Number.isNaN(numValue)) {
          editingValue.value = null;
        } else {
          editingValue.value = numValue;
        }
        break;
      }
      case 'date':
        // Handle empty values
        if (newValue === '' || newValue === null || newValue === undefined) {
          editingValue.value = null;
          return;
        }
        // Date input returns YYYY-MM-DD string, store as UTC midnight ISO.
        // This preserves the exact calendar day without timezone drift.
        if (typeof newValue === 'string' && newValue) {
          editingValue.value = `${newValue}T00:00:00.000Z`;
        } else {
          editingValue.value = null;
        }
        break;
      case 'checkbox':
        // Checkbox returns boolean directly
        editingValue.value = Boolean(newValue);
        break;
      default:
        // Text, list are strings
        // Handle empty values
        if (newValue === '' || newValue === null || newValue === undefined) {
          editingValue.value = null;
        } else {
          editingValue.value = String(newValue);
        }
    }
  },
});

const ticketCustomAttributes = computed(() => {
  if (!props.ticket || !ticketAttributes.value) return [];

  return ticketAttributes.value.map(attribute => {
    const value = props.ticket.custom_attributes?.[attribute.attributeKey];
    return {
      ...attribute,
      value: value,
      hasValue: value !== null && value !== undefined && value !== '',
    };
  });
});

const getAttributeTypeIcon = type => {
  const iconMap = {
    text: 'i-lucide-type',
    number: 'i-lucide-hash',
    date: 'i-lucide-calendar',
    list: 'i-lucide-list',
    checkbox: 'i-lucide-check-square',
  };
  return iconMap[type] || 'i-lucide-tag';
};

const formatAttributeValue = attribute => {
  if (!attribute.hasValue) return '—';

  const { value, attributeDisplayType } = attribute;

  switch (attributeDisplayType) {
    case 'text':
      return value;
    case 'number':
      return Number(value).toLocaleString();
    case 'date':
      return formatDateForDisplay(value);
    case 'list':
      return Array.isArray(value) ? value.join(', ') : value;
    case 'checkbox':
      return value ? 'Sim' : 'Não';
    default:
      return value;
  }
};

const getAttributeValueClass = attribute => {
  if (!attribute.hasValue) return 'text-n-slate-10';

  const { attributeDisplayType } = attribute;

  switch (attributeDisplayType) {
    case 'checkbox':
      return attribute.value ? 'text-green-600' : 'text-red-600';
    default:
      return 'text-n-slate-12';
  }
};

const formatValueForInput = (attribute, value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const { attributeDisplayType } = attribute;

  switch (attributeDisplayType) {
    case 'number': {
      // Convert to number (backend might send as string)
      const num = typeof value === 'number' ? value : Number(value);
      return Number.isNaN(num) ? null : num;
    }
    case 'date':
      return formatDateForInput(value);
    case 'checkbox': {
      // Convert to boolean (backend might send as string "true"/"false")
      return Boolean(value);
    }
    default:
      // Text, list are strings
      return String(value);
  }
};

const startEditing = attribute => {
  editingAttribute.value = attribute;
  editingValue.value = formatValueForInput(attribute, attribute.value);
};

const cancelEditing = () => {
  editingAttribute.value = null;
  editingValue.value = null;
};

const saveAttribute = async () => {
  if (!editingAttribute.value) return;

  try {
    // editingValue is already in the correct type thanks to editingValueModel
    // Just use it directly (null for empty values)
    const valueToSave = editingValue.value;

    const newCustomAttributes = {
      ...props.ticket.custom_attributes,
      [editingAttribute.value.attributeKey]: valueToSave,
    };

    await store.dispatch('tickets/updateCustomAttributes', {
      id: props.ticket.id,
      customAttributes: newCustomAttributes,
    });

    // Refresh ticket history if updateHistory is enabled
    if (props.updateHistory) {
      // Reload ticket to ensure status label is updated after automatic status change
      await store.dispatch('tickets/getTicket', props.ticket.id);
      await store.dispatch('tickets/getTicketHistory', props.ticket.id);
    }

    emit('updated');
    useAlert('Atributo atualizado com sucesso!');
    cancelEditing();
  } catch (error) {
    // Error updating attribute
    useAlert(
      'Erro ao atualizar atributo: ' + (error.message || 'Erro desconhecido')
    );
  }
};
</script>

<template>
  <div class="ticket-custom-attributes">
    <div v-if="showTitle && !compact" class="mb-3">
      <h4 class="text-sm font-medium text-n-slate-11 uppercase tracking-wider">
        {{ t('TICKETS.CUSTOM_ATTRIBUTES') }}
      </h4>
    </div>

    <!-- Loading State -->
    <div v-if="!ticketAttributes" class="text-sm text-n-slate-10">
      {{ t('TICKETS.LOADING_ATTRIBUTES') }}
    </div>

    <!-- No Attributes Defined -->
    <div
      v-else-if="ticketAttributes.length === 0"
      class="text-sm text-n-slate-10"
    >
      {{ t('TICKETS.NO_CUSTOM_ATTRIBUTES') }}
    </div>

    <!-- Attributes List -->
    <div v-else class="space-y-2">
      <div
        v-for="attribute in ticketCustomAttributes"
        :key="attribute.id"
        class="flex flex-col gap-1"
      >
        <!-- Label -->
        <div class="flex items-center gap-1.5">
          <Icon
            :icon="getAttributeTypeIcon(attribute.attributeDisplayType)"
            class="w-3.5 h-3.5 text-n-slate-10 flex-shrink-0"
          />
          <span
            class="text-xs text-n-slate-11 font-medium truncate"
            :title="attribute.attributeDisplayName"
          >
            {{ attribute.attributeDisplayName }}
          </span>
        </div>

        <!-- Value -->
        <div class="w-full">
          <!-- Editing Mode -->
          <div
            v-if="editingAttribute?.id === attribute.id && editable"
            class="w-full"
          >
            <!-- Checkbox Type -->
            <div
              v-if="attribute.attributeDisplayType === 'checkbox'"
              class="flex items-center gap-2 py-1.5"
            >
              <input
                v-model="editingValueModel"
                type="checkbox"
                class="w-4 h-4 text-blue-600 border-n-weak rounded focus:ring-2 focus:ring-blue-500"
              />
              <span class="text-sm text-n-slate-11">
                {{ editingValue ? t('TICKETS.SLA.YES') : t('TICKETS.SLA.NO') }}
              </span>
            </div>

            <!-- List Type -->
            <select
              v-else-if="attribute.attributeDisplayType === 'list'"
              v-model="editingValueModel"
              class="w-full text-sm px-2 py-1.5 border border-n-weak rounded bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{{ t('TICKETS.SELECT_VALUE') }}</option>
              <option
                v-for="option in attribute.attributeValues || []"
                :key="option"
                :value="option"
              >
                {{ option }}
              </option>
            </select>

            <!-- Number Type -->
            <input
              v-else-if="attribute.attributeDisplayType === 'number'"
              v-model="editingValueModel"
              type="number"
              step="any"
              class="w-full text-sm px-2 py-1.5 border border-n-weak rounded bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
              :placeholder="`Digite o valor para ${attribute.attributeDisplayName}`"
              @keyup.enter="saveAttribute"
              @keyup.escape="cancelEditing"
            />

            <!-- Date Type -->
            <input
              v-else-if="attribute.attributeDisplayType === 'date'"
              v-model="editingValueModel"
              type="date"
              class="w-full text-sm px-2 py-1.5 border border-n-weak rounded bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
              :placeholder="`Digite o valor para ${attribute.attributeDisplayName}`"
              @keyup.enter="saveAttribute"
              @keyup.escape="cancelEditing"
            />

            <!-- Text Type -->
            <input
              v-else
              v-model="editingValueModel"
              type="text"
              class="w-full text-sm px-2 py-1.5 border border-n-weak rounded bg-n-solid-3 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-blue-500"
              :placeholder="`Digite o valor para ${attribute.attributeDisplayName}`"
              @keyup.enter="saveAttribute"
              @keyup.escape="cancelEditing"
            />

            <div class="flex gap-1">
              <Button
                slate
                size="sm"
                :disabled="loading"
                :label="$t('TICKETS.BUTTONS.SAVE')"
                @click="saveAttribute"
              />
              <Button
                slate
                outline
                size="sm"
                :label="$t('TICKETS.BUTTONS.CANCEL')"
                @click="cancelEditing"
              />
            </div>
          </div>

          <!-- Display Mode -->
          <template v-else>
            <span
              :class="[
                getAttributeValueClass(attribute),
                editable
                  ? 'cursor-pointer hover:opacity-80 rounded hover:bg-n-solid-4 transition-colors'
                  : '',
                !attribute.hasValue && editable ? 'text-n-slate-10 italic' : '',
              ]"
              class="block break-words text-sm px-2 py-1.5 min-h-[28px]"
              @click="editable && startEditing(attribute)"
            >
              {{ formatAttributeValue(attribute) }}
            </span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
